import SeoHead from '@atoms/SeoHead';
import type { BreadcrumbListSchemaProps } from '@atoms/SeoHead/SeoHead.types';
import type { ArticleSchemaProps } from '@atoms/SeoHead/SeoHead.types';
import { mapArticleSchema } from '@mappers/articleSchema';
import { mapBreadcrumbSchema } from '@mappers/breadcrumbSchema';
import Layout from '@molecules/Layout';
import type { LayoutProps } from '@molecules/Layout/Layout.types';
import type { Heading } from '@molecules/TableOfContents/TableOfContents.types';
import type { PortableTextComponents } from '@portabletext/react';
import PreviewProvider from '@sanity/components/PreviewProvider';
import { getClient } from '@sanity/lib/getClient';
import type { ISanityBody } from '@sanity/types/body';
import type { ISanityPost } from '@sanity/types/post';
import ArticlePageView from '@views/ArticlePageView';
import type { ArticlePageViewProps } from '@views/ArticlePageView/ArticlePageView.types';
import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ReactElement } from 'react';

import { Ad } from '@/components/store/components/Ad/Ad';
import { GENERAL_META_DESCRIPTION } from '@/consts/consts';
// import { changeStringToId } from '@/utils';

interface PostPageProps {
  articleData?: ArticlePageViewProps;
  articleSchema: ArticleSchemaProps;
  breadcrumbSchema: BreadcrumbListSchemaProps;
  layoutData: Omit<LayoutProps, 'children'>;
  metaDescription: string;
  postContent?: ISanityBody[];
  preview: boolean;
  previewToken?: string;
  slug?: string;
  tableOfContents: Heading[];
  title: string;
}

const Post = ({
  articleSchema,
  breadcrumbSchema,
  title,
  metaDescription,
  layoutData,
  articleData,
  postContent,
  tableOfContents,
  preview = false,
  previewToken,
}: PostPageProps): ReactElement => {
  const postContentWithAd: ISanityBody[] = (() => {
    const content = postContent ? [...postContent] : [];
    let h2Count = 0;
    let insertIndex = -1;
    content.forEach((block, i) => {
      const b = block as unknown as { style?: string };
      if (b?.style === 'h2') {
        h2Count += 1;
        if (h2Count === 2) {
          insertIndex = i;
          return;
        }
      }
    });
    if (insertIndex !== -1) {
      content.splice(insertIndex, 0, {
        _type: 'adBlock',
        _key: 'adBlock-1',
      } as unknown as ISanityBody);
    }
    return content;
  })();

  const portableTextComponents: PortableTextComponents = {
    types: {
      adBlock: () => (
        <Ad
          alt="Przewodnik po Teneryfie – atrakcje, plan podróży, planer i porady️."
          destination="/sklep/teneryfa"
          src="/static/images/teneryfa/ad-teneryfa.jpg"
          srcWebp="/static/images/teneryfa/ad-teneryfa.webp"
          mobileOnly
          isSticky={false}
        />
      ),
    },
  };
  if (preview && previewToken) {
    return (
      <PreviewProvider previewToken={previewToken}>
        <SeoHead
          blogSchema={articleSchema}
          breadcrumbSchema={breadcrumbSchema}
          title={title}
          metaDescription={metaDescription}
        />
        <Layout {...layoutData}>
          <ArticlePageView
            {...articleData}
            postContent={postContentWithAd}
            tableOfContents={tableOfContents}
            portableTextComponents={portableTextComponents}
            preview
          />
        </Layout>
      </PreviewProvider>
    );
  }

  return (
    <>
      <SeoHead
        blogSchema={articleSchema}
        breadcrumbSchema={breadcrumbSchema}
        title={title}
        metaDescription={metaDescription}
        image={articleData?.mainImage?.src as string}
      />
      <Layout {...layoutData}>
        <ArticlePageView
          {...articleData}
          postContent={postContentWithAd}
          tableOfContents={tableOfContents}
          portableTextComponents={portableTextComponents}
        />
      </Layout>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const client = getClient();

  const { getAllPosts } = await import('@lib/sanity');
  const posts = await getAllPosts(client);

  const paths = posts.map(post => ({
    params: { slug: post.slug },
  }));

  return {
    paths,
    fallback: false, // if a page is not found, return a 404 page
  };
};

export const getStaticProps: GetStaticProps<PostPageProps> = async ({
  preview = false,
  params,
}) => {
  const previewToken = preview ? process.env.SANITY_READ_TOKEN : ``;
  if (preview && !previewToken) {
    throw new Error(
      `Preview mode is active, but SANITY_READ_TOKEN is not set in environment variables`,
    );
  }
  const client = getClient(previewToken);

  const { getBasicData, getAllCategories, getSinglePost, getAllPosts } =
    await import('@lib/sanity');
  const { mapLayout } = await import('@mappers/layout');
  const { mapArticlePage } = await import('@mappers/articlePage');
  const { mapTableOfContents } = await import('@mappers/tableOfContent');

  const post = await getSinglePost(client, params.slug as string);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const allPosts = await getAllPosts(client);
  const basicData = await getBasicData(client);
  const allCategories = await getAllCategories(client);
  const postContent = post.body;
  const tableOfContents = mapTableOfContents(postContent);

  const MAX_RELATED_POSTS = 3;

  const manualRelatedPosts = (post.relatedPosts || []).filter(
    (relatedPost): relatedPost is ISanityPost => Boolean(relatedPost),
  );

  let relatedPosts: ISanityPost[] = manualRelatedPosts.slice(
    0,
    MAX_RELATED_POSTS,
  );
  const relatedPostIds = new Set([post._id, ...relatedPosts.map(p => p._id)]);

  if (relatedPosts.length < MAX_RELATED_POSTS) {
    const sameCategoryPosts = allPosts.filter(
      singlePostFromAll =>
        !relatedPostIds.has(singlePostFromAll._id) &&
        singlePostFromAll.categories?.some(category =>
          post.categories?.some(
            currentCategory => currentCategory.title === category.title,
          ),
        ),
    );
    const fillFromCategory = sameCategoryPosts.slice(
      0,
      MAX_RELATED_POSTS - relatedPosts.length,
    );
    relatedPosts = relatedPosts.concat(fillFromCategory);
    fillFromCategory.forEach(p => relatedPostIds.add(p._id));
  }

  if (relatedPosts.length < MAX_RELATED_POSTS) {
    const featuredPosts = basicData.featuredPosts;
    const uniqueFeaturedPosts = featuredPosts.filter(
      p => !relatedPostIds.has(p._id),
    );
    relatedPosts = relatedPosts.concat(
      uniqueFeaturedPosts.slice(0, MAX_RELATED_POSTS - relatedPosts.length),
    );
  }

  return {
    props: {
      preview,
      previewToken,
      slug: params.slug,
      title: post.title,
      // TODO: Add meta description to Sanity
      metaDescription: post.excerpt || GENERAL_META_DESCRIPTION,
      breadcrumbSchema: mapBreadcrumbSchema(post),
      articleSchema: mapArticleSchema(post),
      layoutData: {
        ...mapLayout(allCategories),
        showPopup: post.showPopup ?? false,
      },
      tableOfContents,
      articleData: mapArticlePage({
        post,
        otherPosts: relatedPosts,
        ...basicData,
      }),
      postContent,
    } as PostPageProps,
  };
};

export default Post;
